import { PatientNoteService } from './../../../core/services/patient-note.service';
import { FeedbackService } from './../../../core/services/feedback.service';
import { AfterViewChecked, AfterViewInit, ChangeDetectorRef, Component, ElementRef, HostListener, Inject, OnDestroy, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FileObject } from 'src/app/core/interfaces';
import { FileDownloadService } from 'src/app/core/services/file-download.service';

import { environment } from '../../../../environments/environment';

/** A one-finger drag that pages the viewer: 1 forward (dragged left), -1 back (dragged right), 0 when it was not a swipe. */
export function swipeStep(dx: number, dy: number): -1 | 0 | 1 {
  if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(dy) * 1.5) return 0;
  return dx < 0 ? 1 : -1;
}

@Component({
  selector: 'app-image-dialog',
  templateUrl: './image-dialog.component.html',
  styleUrls: ['./image-dialog.component.scss']
})
export class ImageDialogComponent implements AfterViewInit, AfterViewChecked, OnDestroy {
  @ViewChild('imageContainer', { static: false }) imageContainer!: ElementRef;
  @ViewChild('canvasOverlay', { static: false }) canvasOverlay!: ElementRef<HTMLCanvasElement>;
  @ViewChild('image', { static: false }) image!: ElementRef;

  fileObjects: any[];
  imageType: string = '';
  currentIndex = 0;
  scale = 1;
  rotation = 0;

  // Drawing and annotation properties
  drawing = false;
  currentPath: { x: number; y: number }[] = [];
  annotations: any[] = [];
  // We'll clear history on file change
  annotationHistory: any[][] = [];
  historyIndex: number = -1;

  // Annotation style properties
  drawingColor: string = '#FF0000';
  strokeWidth: number = 2;
  textColor: string = '#000000';
  fontSize: number = 16;

  // For dragging text annotations
  selectedTextAnnotation: any = null;
  dragOffset: { x: number, y: number } = { x: 0, y: 0 };

  // One-shot text-placement mode. Activated by the "Add Text" toolbar button so
  // touch users can place text without a double-tap (which would conflict with
  // the drawing gesture on touchstart).
  addingText: boolean = false;

  // Flag to control edit mode
  enableEditing: boolean = false;
  editMode: boolean = false;
  isUploading: boolean = false;

  initialCanvasAdjusted = false;

  panX: number = 0;
  panY: number = 0;
  initialPanX: number = 0;
  initialPanY: number = 0;

  // When true, transforms animate (button-driven). Drag/pinch set it false
  // so the image tracks the finger 1:1 with no lag.
  animateTransform: boolean = false;
  private animateOffTimer: any = null;

  // Pointer Events gesture state — unified across mouse/touch/pen.
  private activePointers: Map<number, { x: number; y: number }> = new Map();
  private gestureStartPanX = 0;
  private gestureStartPanY = 0;
  private gestureStartScale = 1;
  private gestureStartDistance = 0;
  // Anchor position at gesture start, in container-local coordinates.
  private gestureAnchorX = 0;
  private gestureAnchorY = 0;
  // Anchor position in "image-local" coordinates (captured at gesture start
  // so Phase 3 pointer-anchored zoom math works).
  private gestureAnchorLocalX = 0;
  private gestureAnchorLocalY = 0;
  private rafPending = false;
  // Where a touch drag began, so lifting the finger after a sideways drag pages to the next or previous image.
  private swipeStart: { id: number; x: number; y: number } | null = null;

  private readonly MIN_SCALE = 0.5;
  private readonly MAX_SCALE = 10;

  // Keeps the canvas backing store in sync with its rendered size. Entering
  // edit mode inserts the controls bar and shrinks .image-container without
  // firing a window resize, which used to leave the backing store stale.
  private resizeObserver: ResizeObserver | null = null;

  // Bound references so we can removeEventListener on navigate between files.
  private onPointerDownBound = (e: PointerEvent) => this.onPointerDown(e);
  private onPointerMoveBound = (e: PointerEvent) => this.onPointerMove(e);
  private onPointerUpBound = (e: PointerEvent) => this.onPointerUp(e);


  constructor(
    public dialogRef: MatDialogRef<ImageDialogComponent>,
    private feedback: FeedbackService,
    private patientNoteService: PatientNoteService,
    @Inject(MAT_DIALOG_DATA) public data: { fileObjects: FileObject[], selectedFile: FileObject, enableEditing: boolean, index?: number },
    private cdr: ChangeDetectorRef,
    private fileDownloadService: FileDownloadService
  ) {
    this.enableEditing = data.enableEditing;
    this.fileObjects = data.fileObjects;
    const at = data.index ?? (data.selectedFile ? this.fileObjects.findIndex(file => file.url === data.selectedFile.url) : 0);
    this.currentIndex = at >= 0 && at < this.fileObjects.length ? at : 0;
    this.getImageType(this.fileObjects[this.currentIndex]);
  }

  ngAfterViewInit() {
    this.initDefaults();
    this.observeContainerResize();
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
      this.adjustCanvasSize();
    }, 500);
  }

  ngAfterViewChecked() {
    if (!this.initialCanvasAdjusted) {
      this.adjustCanvasSize();
      this.initialCanvasAdjusted = true;
    }
  }

  ngOnDestroy(): void {
    if (this.animateOffTimer) clearTimeout(this.animateOffTimer);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
    if (this.imageContainer?.nativeElement) {
      const container = this.imageContainer.nativeElement as HTMLElement;
      container.removeEventListener('pointerdown', this.onPointerDownBound);
      container.removeEventListener('pointermove', this.onPointerMoveBound);
      container.removeEventListener('pointerup', this.onPointerUpBound);
      container.removeEventListener('pointercancel', this.onPointerUpBound);
      container.removeEventListener('pointerleave', this.onPointerUpBound);
    }
  }

  onImageLoad() {
    requestAnimationFrame(() => {
      this.adjustCanvasSize();
    });
  }

  @HostListener('document:keydown.arrowright', ['$event'])
  handleRightArrow(event: Event) {
    if (!this.editMode) {
      this.nextFile();
    }
  }

  @HostListener('document:keydown.arrowleft', ['$event'])
  handleLeftArrow(event: Event) {
    if (!this.editMode) {
      this.previousFile();
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyDown(event: Event) {
    if (!this.editMode) {
      const e = event as KeyboardEvent;
      if (e.key === '+') {
        this.zoomIn();
      } else if (e.key === '-') {
        this.zoomOut();
      }
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.adjustCanvasSize();
  }

  initDefaults() {
    this.initPanZoom();
    this.initMouseWheelZoom();
  }

  adjustCanvasSize() {
    if (!this.imageContainer || !this.canvasOverlay) return;
    const container = this.imageContainer.nativeElement as HTMLElement;
    const canvas = this.canvasOverlay.nativeElement;
    // clientWidth/Height ignore CSS transforms, so this stays correct even if
    // a pan/zoom transform is still applied to the overlay.
    const width = Math.round(canvas.clientWidth || container.clientWidth);
    const height = Math.round(canvas.clientHeight || container.clientHeight);
    if (!width || !height) return;
    // Writing width/height clears the canvas, so skip the no-op case —
    // otherwise a spurious resize notification wipes an in-progress stroke.
    if (canvas.width === width && canvas.height === height) return;
    canvas.width = width;
    canvas.height = height;
    this.redrawAnnotations();
  }

  // Watch the container so the backing store follows any layout change —
  // entering/leaving edit mode, device rotation, or the on-screen keyboard.
  // None of these fire a window resize on iOS.
  private observeContainerResize() {
    if (this.resizeObserver || typeof ResizeObserver === 'undefined') return;
    if (!this.imageContainer?.nativeElement) return;
    this.resizeObserver = new ResizeObserver(() => this.adjustCanvasSize());
    this.resizeObserver.observe(this.imageContainer.nativeElement);
  }

  // Annotation coordinates are stored normalised against this box: the canvas
  // as actually rendered, in CSS pixels. Every hit-test and the export mapping
  // must use it — NOT canvas.width/height, which is the backing store and can
  // lag a layout change by a frame (or entirely, on browsers without
  // ResizeObserver). Mixing the two is what shifted saved annotations.
  private getCanvasRect(): DOMRect {
    return this.canvasOverlay.nativeElement.getBoundingClientRect();
  }

  // Hit box for a text annotation in the same CSS-pixel space as getCanvasRect().
  // `pad` widens the target for touch, where a finger is far less precise than
  // a cursor.
  private getTextBounds(annotation: any, width: number, height: number, pad: number = 0) {
    const fontSize = annotation.fontSize || this.fontSize;
    const ctx = this.canvasOverlay.nativeElement.getContext('2d');
    // Measure the real glyph run; text.length * 0.6 badly misjudges anything
    // that isn't average-width lowercase.
    let textWidth = annotation.text.length * fontSize * 0.6;
    if (ctx) {
      const prevFont = ctx.font;
      ctx.font = `${fontSize}px sans-serif`;
      textWidth = ctx.measureText(annotation.text).width;
      ctx.font = prevFont;
    }
    const left = annotation.x * width;
    const baseline = annotation.y * height;
    return {
      left: left - pad,
      right: left + textWidth + pad,
      top: baseline - fontSize - pad,
      bottom: baseline + pad,
      fontSize
    };
  }

  private hitTestText(clickX: number, clickY: number, width: number, height: number, pad: number) {
    // Reverse order so the most recently placed text wins when two overlap.
    for (let i = this.annotations.length - 1; i >= 0; i--) {
      const annotation = this.annotations[i];
      if (annotation.type !== 'text') continue;
      const b = this.getTextBounds(annotation, width, height, pad);
      if (clickX >= b.left && clickX <= b.right && clickY >= b.top && clickY <= b.bottom) {
        return { annotation, bounds: b };
      }
    }
    return null;
  }

  /** "2 of 7" while there is more than one image; empty otherwise. */
  get position(): string {
    return this.fileObjects.length > 1 ? `${this.currentIndex + 1} of ${this.fileObjects.length}` : '';
  }

  nextFile() {
    this.currentIndex = (this.currentIndex + 1) % this.fileObjects.length;
    // Reset transformations and clear annotation state for new file.
    this.resetTransformations();
    this.clearAnnotations();
    this.getImageType(this.fileObjects[this.currentIndex]);
  }

  previousFile() {
    this.currentIndex = (this.currentIndex - 1 + this.fileObjects.length) % this.fileObjects.length;
    this.resetTransformations();
    this.clearAnnotations();
    this.getImageType(this.fileObjects[this.currentIndex]);
  }

  resetTransformations() {
    this.scale = 1;
    this.rotation = 0;
    this.panX = 0;
    this.panY = 0;
    this.activePointers.clear();
    this.applyTransform();
    // Listeners are already bound from ngAfterViewInit; don't re-register.
  }

  private wheelListenerBound = false;
  initMouseWheelZoom() {
    if (this.wheelListenerBound) return;
    this.wheelListenerBound = true;
    const container = this.imageContainer.nativeElement as HTMLElement;
    // passive:false is required — the default is passive on modern browsers,
    // which would silently ignore our preventDefault() and scroll the page.
    container.addEventListener('wheel', (event: WheelEvent) => {
      if (this.editMode) { return; }
      event.preventDefault();

      const oldScale = this.scale;
      const step = event.deltaY > 0 ? -0.1 : 0.1;
      const newScale = Math.max(this.MIN_SCALE, Math.min(this.MAX_SCALE, oldScale + step));
      if (newScale === oldScale) return;

      // Anchor zoom to the wheel pointer so the point under the cursor
      // stays put. Uses the default transform-origin (center) inversion.
      const rect = container.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;
      const pointerContainerX = event.clientX - rect.left;
      const pointerContainerY = event.clientY - rect.top;
      // Convert pointer to image-local coords at current transform.
      const localX = cx + (pointerContainerX - cx - this.panX) / oldScale;
      const localY = cy + (pointerContainerY - cy - this.panY) / oldScale;

      this.scale = newScale;
      // Rearrange the forward transform to solve for the new pan.
      this.panX = pointerContainerX - cx - (localX - cx) * newScale;
      this.panY = pointerContainerY - cy - (localY - cy) * newScale;
      this.applyTransform();
    }, { passive: false });
  }

  initPanZoom() {
    if (!this.imageContainer || !this.image) return;
    const container = this.imageContainer.nativeElement as HTMLElement;

    // Pointer Events unify mouse/touch/pen in a single code path. Combined
    // with touch-action: none on the container (see SCSS), this stops the
    // browser from hijacking gestures on iOS.
    container.removeEventListener('pointerdown', this.onPointerDownBound);
    container.removeEventListener('pointermove', this.onPointerMoveBound);
    container.removeEventListener('pointerup', this.onPointerUpBound);
    container.removeEventListener('pointercancel', this.onPointerUpBound);
    container.removeEventListener('pointerleave', this.onPointerUpBound);

    container.addEventListener('pointerdown', this.onPointerDownBound);
    container.addEventListener('pointermove', this.onPointerMoveBound);
    container.addEventListener('pointerup', this.onPointerUpBound);
    container.addEventListener('pointercancel', this.onPointerUpBound);
    container.addEventListener('pointerleave', this.onPointerUpBound);
  }

  private onPointerDown(event: PointerEvent): void {
    if (this.editMode) return; // Drawing owns pointers in edit mode.

    const container = this.imageContainer.nativeElement as HTMLElement;
    // Capture this pointer so we keep receiving moves even if the finger
    // drifts outside the container (essential on iOS).
    try { container.setPointerCapture(event.pointerId); } catch { /* ignore */ }

    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    this.swipeStart = event.pointerType !== 'mouse' && this.activePointers.size === 1
      ? { id: event.pointerId, x: event.clientX, y: event.clientY }
      : null;

    // Start or restart the gesture baseline whenever the pointer count changes.
    this.beginGesture();
  }

  private onPointerMove(event: PointerEvent): void {
    if (this.editMode) return;
    if (!this.activePointers.has(event.pointerId)) return;

    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    const count = this.activePointers.size;
    if (count === 1) {
      // Single-pointer pan.
      const [p] = Array.from(this.activePointers.values());
      const dx = p.x - this.gestureAnchorX;
      const dy = p.y - this.gestureAnchorY;
      this.panX = this.gestureStartPanX + dx;
      this.panY = this.gestureStartPanY + dy;
      this.scheduleTransform();
    } else if (count >= 2) {
      // Two-pointer pinch (+ optional pan of the midpoint).
      const pts = Array.from(this.activePointers.values()).slice(0, 2);
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);

      if (this.gestureStartDistance > 0) {
        const raw = this.gestureStartScale * (dist / this.gestureStartDistance);
        const newScale = Math.max(this.MIN_SCALE, Math.min(this.MAX_SCALE, raw));
        this.scale = newScale;

        // Phase 3: keep the pinch midpoint anchored. The local point captured
        // at gesture start (in image coordinates, before transform) must end
        // up back under the current midpoint.
        const rect = (this.imageContainer.nativeElement as HTMLElement).getBoundingClientRect();
        const midContainerX = midX - rect.left;
        const midContainerY = midY - rect.top;
        // Inverse of: screen = center + pan + (local - center) * scale
        // (because transform-origin defaults to 50% 50%).
        const cx = rect.width / 2;
        const cy = rect.height / 2;
        this.panX = midContainerX - cx - (this.gestureAnchorLocalX - cx) * this.scale;
        this.panY = midContainerY - cy - (this.gestureAnchorLocalY - cy) * this.scale;
      }
      this.scheduleTransform();
    }
  }

  private onPointerUp(event: PointerEvent): void {
    if (this.editMode) return;
    const container = this.imageContainer.nativeElement as HTMLElement;
    try { container.releasePointerCapture(event.pointerId); } catch { /* ignore */ }
    this.activePointers.delete(event.pointerId);
    const start = this.swipeStart;
    this.swipeStart = null;
    // Swiping pages only at normal size; a zoomed image is being moved around instead.
    if (start?.id === event.pointerId && this.scale <= 1 && this.fileObjects.length > 1) {
      const step = swipeStep(event.clientX - start.x, event.clientY - start.y);
      if (step === 1) this.nextFile();
      else if (step === -1) this.previousFile();
      if (step) this.cdr.detectChanges();
    }
    // Re-baseline so the remaining pointer (if any) continues smoothly.
    this.beginGesture();
  }

  // Capture the current pan/scale and pointer anchor so subsequent moves can
  // compute deltas. Called on each pointer up/down so gestures cleanly hand
  // off between 1- and 2-finger modes.
  private beginGesture(): void {
    this.gestureStartPanX = this.panX;
    this.gestureStartPanY = this.panY;
    this.gestureStartScale = this.scale;

    const pts = Array.from(this.activePointers.values());
    const rect = (this.imageContainer.nativeElement as HTMLElement).getBoundingClientRect();
    const cx = rect.width / 2;
    const cy = rect.height / 2;

    if (pts.length === 1) {
      this.gestureAnchorX = pts[0].x;
      this.gestureAnchorY = pts[0].y;
      this.gestureStartDistance = 0;
    } else if (pts.length >= 2) {
      const midX = (pts[0].x + pts[1].x) / 2;
      const midY = (pts[0].y + pts[1].y) / 2;
      this.gestureAnchorX = midX;
      this.gestureAnchorY = midY;
      this.gestureStartDistance = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
      // Convert pinch midpoint into image-local coordinates at the CURRENT
      // transform. Invert: local = center + (screen - center - pan) / scale.
      const midContainerX = midX - rect.left;
      const midContainerY = midY - rect.top;
      this.gestureAnchorLocalX = cx + (midContainerX - cx - this.panX) / this.scale;
      this.gestureAnchorLocalY = cy + (midContainerY - cy - this.panY) / this.scale;
    } else {
      this.gestureStartDistance = 0;
    }
  }

  // Coalesce rapid pointermove updates into one transform write per frame.
  private scheduleTransform(): void {
    if (this.rafPending) return;
    this.rafPending = true;
    requestAnimationFrame(() => {
      this.rafPending = false;
      this.applyTransform(false);
    });
  }

  getImageType(fileObject: any): string {
    if (!fileObject || !fileObject.url) return '';

    const url = fileObject.url.toLowerCase();
    const typeMap = {
      communication: '-communication',
      consent: '-consent',
    };

    const foundType = Object.entries(typeMap).find(([_, pattern]) => url.includes(pattern));
    this.imageType = foundType ? foundType[0] : 'note';

    return this.imageType;
  }

  getFileUrl(fileObject: any): string {
    return URL.createObjectURL(fileObject);
  }

  zoomIn() {
    if (!this.editMode) {
      this.scale += 0.1;
      this.applyTransform(true);
    }
  }

  zoomOut() {
    if (!this.editMode && this.scale > 0.1) {
      this.scale -= 0.1;
      this.applyTransform(true);
    }
  }

  resetZoom() {
    if (!this.editMode) {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.applyTransform(true);
    }
  }

  rotateLeft() {
    if (!this.editMode) {
      this.rotation -= 90;
      this.applyTransform(true);
    }
  }

  rotateRight() {
    if (!this.editMode) {
      this.rotation += 90;
      this.applyTransform(true);
    }
  }

  applyTransform(animate: boolean = false) {
    const transformStr = `translate(${this.panX}px, ${this.panY}px) scale(${this.scale}) rotate(${this.rotation}deg)`;
    this.image.nativeElement.style.transform = transformStr;
    this.canvasOverlay.nativeElement.style.transform = transformStr;

    if (animate) {
      this.animateTransform = true;
      if (this.animateOffTimer) clearTimeout(this.animateOffTimer);
      // Slightly longer than the CSS transition to guarantee it completes.
      this.animateOffTimer = setTimeout(() => {
        this.animateTransform = false;
        this.animateOffTimer = null;
      }, 220);
    } else if (this.animateTransform) {
      // Any interactive update should immediately kill the animation class so
      // the image tracks the finger with no lag.
      this.animateTransform = false;
      if (this.animateOffTimer) {
        clearTimeout(this.animateOffTimer);
        this.animateOffTimer = null;
      }
    }
  }


  // DRAWING AND TEXT METHODS

  startDrawing(event: MouseEvent | TouchEvent) {
    if (!this.editMode) { return; }
    this.drawing = true;
    this.currentPath = [];
    this.addPoint(event);
  }

  addPoint(event: MouseEvent | TouchEvent) {
    if (!this.editMode) { return; }
    const canvasRect = this.getCanvasRect();
    const { clientX, clientY } = this.getEventClientXY(event);
    const x = (clientX - canvasRect.left) / canvasRect.width;  // relative x
    const y = (clientY - canvasRect.top) / canvasRect.height;   // relative y
    this.currentPath.push({ x, y });
    this.redrawAnnotations();
  }

  // Safely extract clientX/Y from mouse, touch, or pointer events.
  // `instanceof TouchEvent` is unreliable in some WebViews where the
  // constructor isn't on window (throws ReferenceError silently).
  private getEventClientXY(event: MouseEvent | TouchEvent): { clientX: number; clientY: number } {
    if ('touches' in event && event.touches && event.touches.length) {
      return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
    }
    if ('changedTouches' in event && event.changedTouches && event.changedTouches.length) {
      return { clientX: event.changedTouches[0].clientX, clientY: event.changedTouches[0].clientY };
    }
    const me = event as MouseEvent;
    return { clientX: me.clientX, clientY: me.clientY };
  }

  stopDrawing() {
    if (!this.editMode) { return; }
    this.drawing = false;
    if (this.currentPath.length) {
      this.annotations.push({
        type: 'stroke',
        path: this.currentPath,
        drawingColor: this.drawingColor,
        strokeWidth: this.strokeWidth
      });
      this.currentPath = [];
      this.recordHistory();
      this.redrawAnnotations();
    }
  }

  onCanvasDblClick(event: MouseEvent | TouchEvent) {
    if (!this.editMode) { return; }
    this.placeTextAt(event);
  }

  toggleAddText() {
    if (!this.editMode) { return; }
    this.addingText = !this.addingText;
  }

  private placeTextAt(event: MouseEvent | TouchEvent) {
    const rect = this.getCanvasRect();
    const { clientX, clientY } = this.getEventClientXY(event);
    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    const text = prompt('Enter text:');
    if (text) {
      this.annotations.push({
        type: 'text',
        text,
        x,
        y,
        textColor: this.textColor,
        fontSize: this.fontSize
      });
      this.recordHistory();
      this.redrawAnnotations();
    }
  }

  // TOUCH SUPPORT: map touch events to mouse handlers

  onCanvasTouchStart(event: TouchEvent) {
    event.preventDefault();
    // Delegate entirely: onCanvasMouseDown handles the add-text mode, hit-tests
    // existing text, and only starts a stroke when nothing was grabbed. Calling
    // startDrawing() here as well used to lay down a stray stroke under the
    // finger whenever the user tried to drag a text annotation.
    this.onCanvasMouseDown(event as unknown as MouseEvent);
  }

  onCanvasTouchMove(event: TouchEvent) {
    event.preventDefault();
    this.onCanvasMouseMove(event as unknown as MouseEvent);
  }

  onCanvasTouchEnd(event: TouchEvent) {
    event.preventDefault();
    this.onCanvasMouseUp(event as unknown as MouseEvent);
  }

  // TEXT DRAGGING METHODS

  onCanvasMouseDown(event: MouseEvent | TouchEvent) {
    if (!this.editMode) { return; }
    if (this.addingText) {
      this.placeTextAt(event);
      this.addingText = false;
      return;
    }
    const rect = this.getCanvasRect();
    const { clientX, clientY } = this.getEventClientXY(event);
    const clickX = clientX - rect.left;
    const clickY = clientY - rect.top;
    const isTouch = 'touches' in event || 'changedTouches' in event;

    const hit = this.hitTestText(clickX, clickY, rect.width, rect.height, isTouch ? 10 : 0);
    if (hit) {
      this.selectedTextAnnotation = hit.annotation;
      // Offset from the text's true top-left, not the padded hit box, so the
      // annotation doesn't jump by `pad` the moment the drag starts.
      this.dragOffset.x = clickX - hit.annotation.x * rect.width;
      this.dragOffset.y = clickY - (hit.annotation.y * rect.height - hit.bounds.fontSize);
      // Save original position for history (for undo/redo)
      this.selectedTextAnnotation.originalX = hit.annotation.x;
      this.selectedTextAnnotation.originalY = hit.annotation.y;
      return;
    }
    // Not over text — start a drawing stroke.
    this.startDrawing(event);
  }

  onCanvasMouseMove(event: MouseEvent | TouchEvent) {
    if (!this.editMode) { return; }
    const rect = this.getCanvasRect();
    const canvas = this.canvasOverlay.nativeElement;
    const { clientX, clientY } = this.getEventClientXY(event);
    const pointerX = clientX - rect.left;
    const pointerY = clientY - rect.top;

    if (this.addingText) {
      canvas.style.cursor = 'crosshair';
      return;
    }

    // Provide user feedback if hovering over a text annotation:
    if (!this.drawing && !this.selectedTextAnnotation) {
      const hover = this.hitTestText(pointerX, pointerY, rect.width, rect.height, 0);
      canvas.style.cursor = hover ? 'pointer' : 'default';
    }

    if (this.selectedTextAnnotation) {
      // Update position for dragging text.
      const newAnnotX = pointerX - this.dragOffset.x;
      const newAnnotY = pointerY - this.dragOffset.y;
      const fontSize = this.selectedTextAnnotation.fontSize || this.fontSize;
      this.selectedTextAnnotation.x = newAnnotX / rect.width;
      this.selectedTextAnnotation.y = (newAnnotY + fontSize) / rect.height;
      this.redrawAnnotations();
    } else if (this.drawing) {
      this.addPoint(event);
    }
  }

  onCanvasMouseUp(event: MouseEvent | TouchEvent) {
    if (!this.editMode) { return; }
    if (this.selectedTextAnnotation) {
      // If position changed, record a history snapshot.
      if (this.selectedTextAnnotation.originalX !== this.selectedTextAnnotation.x ||
        this.selectedTextAnnotation.originalY !== this.selectedTextAnnotation.y) {
        this.recordHistory();
      }
      this.selectedTextAnnotation = null;
    } else if (this.drawing) {
      this.stopDrawing();
    }
  }

  recordHistory() {
    try {
      // Remove future snapshots if any.
      this.annotationHistory = this.annotationHistory.slice(0, this.historyIndex + 1);
      // Deep-copy annotations
      this.annotationHistory.push(JSON.parse(JSON.stringify(this.annotations)));
      this.historyIndex++;
    } catch (error) {
      console.error('Error recording history:', error);
    }
  }

  undoAnnotation() {
    if (this.historyIndex > 0) {
      this.historyIndex--;
      this.annotations = JSON.parse(JSON.stringify(this.annotationHistory[this.historyIndex]));
      this.redrawAnnotations();
    }
  }

  redoAnnotation() {
    if (this.historyIndex < this.annotationHistory.length - 1) {
      this.historyIndex++;
      this.annotations = JSON.parse(JSON.stringify(this.annotationHistory[this.historyIndex]));
      this.redrawAnnotations();
    }
  }

  clearAnnotations() {
    this.annotations = [];
    this.annotationHistory = [];
    this.historyIndex = -1;
    this.redrawAnnotations();
  }

  redrawAnnotations() {
    const canvas = this.canvasOverlay.nativeElement;
    const ctx = canvas.getContext('2d');
    if (!ctx) { return; }
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    this.annotations.forEach(annotation => {
      if (annotation.type === 'stroke') {
        ctx.strokeStyle = annotation.drawingColor || this.drawingColor;
        ctx.lineWidth = annotation.strokeWidth || this.strokeWidth;
        ctx.beginPath();
        annotation.path.forEach((point: { x: number; y: number }, index: number) => {
          // Draw using base coordinates
          const xPos = point.x * canvas.width;
          const yPos = point.y * canvas.height;
          index === 0 ? ctx.moveTo(xPos, yPos) : ctx.lineTo(xPos, yPos);
        });
        ctx.stroke();
      } else if (annotation.type === 'text') {
        ctx.fillStyle = annotation.textColor || this.textColor;
        ctx.font = `${annotation.fontSize || this.fontSize}px sans-serif`;
        const xPos = annotation.x * canvas.width;
        const yPos = annotation.y * canvas.height;
        ctx.fillText(annotation.text, xPos, yPos);
      }
    });

    // Draw in-progress stroke if any.
    if (this.drawing && this.currentPath.length) {
      ctx.strokeStyle = this.drawingColor;
      ctx.lineWidth = this.strokeWidth;
      ctx.beginPath();
      this.currentPath.forEach((point, index) => {
        const xPos = point.x * canvas.width;
        const yPos = point.y * canvas.height;
        index === 0 ? ctx.moveTo(xPos, yPos) : ctx.lineTo(xPos, yPos);
      });
      ctx.stroke();
    }
  }

  async saveDrawing() {
    if (this.isUploading) return;
    this.isUploading = true;

    const imgEl = this.image.nativeElement;

    const naturalWidth = imgEl.naturalWidth;
    const naturalHeight = imgEl.naturalHeight;
    // Annotations are normalised against the canvas as displayed, so the
    // object-fit: contain letterbox maths below has to be done in that same
    // box. Using the backing store (canvasEl.width/height) shifted every
    // annotation whenever the two had drifted apart — which is exactly what
    // entering edit mode does, since the controls bar shrinks the container.
    const canvasRect = this.getCanvasRect();
    const canvasWidth = canvasRect.width;
    const canvasHeight = canvasRect.height;
    if (!naturalWidth || !naturalHeight || !canvasWidth || !canvasHeight) {
      this.feedback.showToast('Could not save annotation — image not ready.');
      this.isUploading = false;
      return;
    }

    const imgAspect = naturalWidth / naturalHeight;
    const canvasAspect = canvasWidth / canvasHeight;

    let renderWidth = canvasWidth;
    let renderHeight = canvasHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (imgAspect > canvasAspect) {
      renderWidth = canvasWidth;
      renderHeight = canvasWidth / imgAspect;
      offsetY = (canvasHeight - renderHeight) / 2;
    } else {
      renderHeight = canvasHeight;
      renderWidth = canvasHeight * imgAspect;
      offsetX = (canvasWidth - renderWidth) / 2;
    }

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = naturalWidth;
    exportCanvas.height = naturalHeight;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) {
      this.isUploading = false;
      return;
    }

    const baseImage = new Image();
    baseImage.crossOrigin = 'anonymous';

    // Without this the spinner spins forever on a load failure and the doctor
    // has no way back other than closing the dialog (losing the annotations).
    baseImage.onerror = () => {
      console.error('[Annotation Save] base image failed to load');
      this.feedback.showToast('Could not load the image to save onto.');
      this.isUploading = false;
    };

    baseImage.onload = async () => {
      ctx.drawImage(baseImage, 0, 0, naturalWidth, naturalHeight);

      // ⬇️ Draw annotations
      this.annotations.forEach(annotation => {
        if (annotation.type === 'stroke') {
          ctx.strokeStyle = annotation.drawingColor || this.drawingColor;
          ctx.lineWidth = (annotation.strokeWidth || this.strokeWidth) * (naturalWidth / renderWidth);
          ctx.beginPath();
          annotation.path.forEach((point: { x: number, y: number }, index: number) => {
            const xCanvas = point.x * canvasWidth;
            const yCanvas = point.y * canvasHeight;
            const xRel = (xCanvas - offsetX) / renderWidth;
            const yRel = (yCanvas - offsetY) / renderHeight;
            const x = xRel * naturalWidth;
            const y = yRel * naturalHeight;
            index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          ctx.stroke();
        } else if (annotation.type === 'text') {
          ctx.fillStyle = annotation.textColor || this.textColor;
          const fontSize = (annotation.fontSize || this.fontSize) * (naturalHeight / renderHeight);
          ctx.font = `${fontSize}px sans-serif`;

          const xCanvas = annotation.x * canvasWidth;
          const yCanvas = annotation.y * canvasHeight;
          const xRel = (xCanvas - offsetX) / renderWidth;
          const yRel = (yCanvas - offsetY) / renderHeight;
          const x = xRel * naturalWidth;
          const y = yRel * naturalHeight;

          ctx.fillText(annotation.text, x, y);
        }
      });

      exportCanvas.toBlob(async (annotatedBlob) => {
        if (!annotatedBlob) {
          this.feedback.showToast('Failed to render the annotated image.');
          this.isUploading = false;
          return;
        }

        // If original image isn't persisted yet, defer annotation upload
        const original = this.fileObjects[this.currentIndex];
        if (!original.id) {
          // Replace the original file's blob and URL
          const fileUrl = URL.createObjectURL(annotatedBlob);
          original.originalUrl = original.url;
          original.url = fileUrl;
          original.type = 'image/png';
          original.uploaded = false;
          original.annotatedVersions = []; // Clean up
          this.feedback.showToast('Edited image will be saved with the note.');
          this.cleanupAfterSave();
          return;
        }

        // Otherwise, upload immediately
        this.patientNoteService.uploadAnnotatedMedia(annotatedBlob, original).subscribe({
          next: () => {
            this.feedback.showToast('Annotation uploaded!');
            this.cleanupAfterSave();
          },
          error: (err) => {
            console.error('[Annotation Upload Failed]', err);
            this.feedback.showToast('Failed to upload annotation.');
            this.isUploading = false;
          }
        });
      }, 'image/png');
    };

    // Assigned last so onload/onerror are attached before a cached image can
    // fire them synchronously.
    baseImage.src = imgEl.src;
  }

  private cleanupAfterSave() {
    this.editMode = false;
    this.drawing = false;
    this.addingText = false;
    this.isUploading = false;
  }


  /* // Save and persist annotations.
  async saveDrawing() {
    if (this.isUploading) return;
    this.isUploading = true;
    const imgEl = this.image.nativeElement;
    const canvasEl = this.canvasOverlay.nativeElement;

    const naturalWidth = imgEl.naturalWidth;
    const naturalHeight = imgEl.naturalHeight;
    const canvasWidth = canvasEl.width;
    const canvasHeight = canvasEl.height;

    // Get how the image is rendered inside the canvas (with object-fit: contain)
    const imgAspect = naturalWidth / naturalHeight;
    const canvasAspect = canvasWidth / canvasHeight;

    let renderWidth = canvasWidth;
    let renderHeight = canvasHeight;
    let offsetX = 0;
    let offsetY = 0;

    if (imgAspect > canvasAspect) {
      // Image fills width, letterboxed vertically
      renderWidth = canvasWidth;
      renderHeight = canvasWidth / imgAspect;
      offsetY = (canvasHeight - renderHeight) / 2;
    } else {
      // Image fills height, letterboxed horizontally
      renderHeight = canvasHeight;
      renderWidth = canvasHeight * imgAspect;
      offsetX = (canvasWidth - renderWidth) / 2;
    }

    // Create export canvas at natural image resolution
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = naturalWidth;
    exportCanvas.height = naturalHeight;
    const ctx = exportCanvas.getContext('2d');
    if (!ctx) return;

    const baseImage = new Image();
    baseImage.crossOrigin = 'anonymous';
    baseImage.src = imgEl.src;

    baseImage.onload = () => {
      ctx.drawImage(baseImage, 0, 0, naturalWidth, naturalHeight);

      this.annotations.forEach(annotation => {
        if (annotation.type === 'stroke') {
          ctx.strokeStyle = annotation.drawingColor || this.drawingColor;
          ctx.lineWidth = (annotation.strokeWidth || this.strokeWidth) * (naturalWidth / renderWidth);

          ctx.beginPath();
          annotation.path.forEach((point: { x: number, y: number }, index: number) => {
            // Adjust relative coords from visible canvas space -> image space
            const xCanvas = point.x * canvasWidth;
            const yCanvas = point.y * canvasHeight;

            const xRel = (xCanvas - offsetX) / renderWidth;
            const yRel = (yCanvas - offsetY) / renderHeight;

            const x = xRel * naturalWidth;
            const y = yRel * naturalHeight;

            index === 0 ? ctx.moveTo(x, y) : ctx.lineTo(x, y);
          });
          ctx.stroke();
        } else if (annotation.type === 'text') {
          ctx.fillStyle = annotation.textColor || this.textColor;
          const fontSize = (annotation.fontSize || this.fontSize) * (naturalHeight / renderHeight);
          ctx.font = `${fontSize}px sans-serif`;

          const xCanvas = annotation.x * canvasWidth;
          const yCanvas = annotation.y * canvasHeight;

          const xRel = (xCanvas - offsetX) / renderWidth;
          const yRel = (yCanvas - offsetY) / renderHeight;

          const x = xRel * naturalWidth;
          const y = yRel * naturalHeight;

          ctx.fillText(annotation.text, x, y);
        }
      });

      // Trigger browser download
      // exportCanvas.toBlob(blob => {
      //  if (!blob) return;
      //  const link = document.createElement('a');
      //  link.href = URL.createObjectURL(blob);
      //  link.download = `annotated-${Date.now()}.png`;
      //  link.click();
      //  URL.revokeObjectURL(link.href);
      //}, 'image/png'); 

      exportCanvas.toBlob((blob) => {
        if (!blob) return;

        const original = this.fileObjects[this.currentIndex];

        if (!original.id) {
          // Queue blob locally for deferred save
          original.annotatedVersions = original.annotatedVersions || [];
          original.annotatedVersions.push({ blob, fileName: `annotated-${Date.now()}.png` });
          //this.feedback.showToast('Annotation will be saved with the note.');
          this.editMode = false;
          this.drawing = false;
          this.isUploading = false;
          return;
        }

        this.patientNoteService.uploadAnnotatedMedia(blob, original).subscribe({
          next: (response) => {
            this.feedback.showToast('Annotation uploaded!');
            this.editMode = false;
            this.drawing = false;
            this.isUploading = false;
          },
          error: (err) => {
            console.error('[Annotation Upload Failed]', err);
            this.feedback.showToast('Failed to upload annotation.');
            this.isUploading = false;
          }
        });
      }, 'image/png');

      this.editMode = false;
      this.drawing = false;
    };
  } */


  cancelDrawing() {
    this.clearAnnotations();
    this.editMode = false;
    this.drawing = false;
    this.addingText = false;
    // Hiding the controls bar grows the container back.
    this.syncCanvasAfterLayoutChange();
  }



  // Toggle edit mode.
  enableEditMode() {
    if (this.imageType === 'note') {
      this.resetTransformations();
      this.editMode = true;
      // Showing the controls bar shrinks .image-container. ResizeObserver
      // normally catches this; this is the fallback for WebViews without it.
      this.syncCanvasAfterLayoutChange();
    } else {
      this.feedback.showToast('Editing is only allowed for patient note images.');
    }
  }

  // Re-measure once Angular has rendered the toggled controls and the browser
  // has laid them out. adjustCanvasSize() no-ops if nothing actually changed.
  private syncCanvasAfterLayoutChange() {
    this.cdr.detectChanges();
    requestAnimationFrame(() => this.adjustCanvasSize());
  }

  async downloadFile() {
    const file = this.fileObjects[this.currentIndex];
    const url = file.originalPdfUrl || file.url;

    const pathWithoutQuery = url.split('?')[0];
    const extension = pathWithoutQuery.split('.').pop()?.toLowerCase();

    // Use proxy only for non-PDF files
    if (extension !== 'pdf') {
      try {
        const proxyUrl = `${environment.API_URL}/api/image-proxy?url=${encodeURIComponent(url)}`;
        const response = await fetch(proxyUrl);
        const data = await response.json();

        if (!data.base64Image) throw new Error('No image returned from proxy');

        const byteCharacters = atob(data.base64Image);
        const byteNumbers = new Array(byteCharacters.length).fill(0).map((_, i) => byteCharacters.charCodeAt(i));
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: file.type });

        await this.fileDownloadService.download(blob, file.name || `image.${extension || 'png'}`);
      } catch (error) {
        console.error('Image download failed:', error);
        this.feedback.showToast('Failed to download image.');
      }
    } else {
      // Standard PDF download — fetch via the same path, then platform-aware save
      await this.fileDownloadService.downloadFromUrl(url, file.name || 'file.pdf');
    }
  }



  closeModal() {
    this.dialogRef.close({ fileObjects: this.fileObjects });
  }
}
